import React, { useState, useEffect } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface ConsultationForm {
  topic: string;
  name: string;
  email: string;
}

const QuestionAndFreeConsultation: React.FC = () => {
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  const [formData, setFormData] = useState<ConsultationForm>({
    topic: '',
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // FAQ data array
  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: " How are digital platforms helping modern cow dung enterprises manage supply chains and product logistics efficiently?",
      answer: "Digital platforms help cow dung enterprises by enabling real-time tracking, online orders, digital payments, route optimization, and better communication, making their supply chain and logistics faster, transparent, and more efficient."
    },
    {
      id: 2,
      question: "With the rise of Al-driven scams in agriculture, how can blockchain ensure the authenticity of organic cow dung products and prevent fake certifications?",
      answer: "Blockchain ensures the authenticity of organic cow dung products by recording every step of production and certification on a tamper-proof digital ledger. This transparency prevents fake certifications, verifies product origin, and builds trust between farmers, buyers, and regulators."
    },
    {
      id: 3,
      question: "In an era of increasing trade regionalization (e.g., USMCA, RCEP), how do Digital Tariffs differ from Carbon Border Adjustment Mechanisms as protectionist tools?",
      answer: "The Core Question: This distinguishes between a Digital Tariff (a tax on cross-border data flows or digital services) and a Carbon Border Tax (a levy on imports based on their carbon footprint during production). Both act as new-age trade barriers but target fundamentally different policy goals (data sovereignty vs. climate change)."
    },
    {
      id: 4,
      question: "Beyond currency and payment risks, how are exporters mitigating Climate Logistics Risk and Geopolitical Decoupling Risk in their supply chains?",
      answer: "The 2025 Context: Standard risk management focused on finances. Today, physical and geopolitical disruptions are paramount."
    }
  ];

  // Consultation topics
  const consultationTopics = [
    "Import",
    "Export",
  ];

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all elements with animation classes
    const animatedElements = document.querySelectorAll(
      '.animate-left, .animate-right, .animate-top, .animate-bottom, .animate-fade-in'
    );
    
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const toggleQuestion = (id: number) => {
    setActiveQuestion(activeQuestion === id ? null : id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const sendToWhatsApp = (data: ConsultationForm): { success: boolean; message: string } => {
    try {
      const whatsappNumber = '919975922007'; // Added country code 91
      
      // Format the message for WhatsApp
      const whatsappMessage = `
🆕 *NEW CONSULTATION REQUEST*

*Topic:* ${data.topic}
*Name:* ${data.name}
*Email:* ${data.email}

*Submitted on:* ${new Date().toLocaleString()}

Please respond to this consultation request at your earliest convenience.
      `.trim();

      // Create WhatsApp URL
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      
      // Open WhatsApp in a new tab
      window.open(whatsappUrl, '_blank');
      
      return {
        success: true,
        message: 'Consultation request sent successfully! We will contact you shortly via WhatsApp.'
      };
    } catch (error) {
      console.error('Error sending to WhatsApp:', error);
      return {
        success: false,
        message: 'Failed to send consultation request. Please try again.'
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Send data to WhatsApp
      const { success, message } = sendToWhatsApp(formData);
      
      if (success) {
        setSubmitMessage({
          type: 'success',
          message: message
        });
        
        // Reset form after successful submission
        setFormData({ 
          topic: '', 
          name: '', 
          email: '' 
        });
      } else {
        setSubmitMessage({
          type: 'error',
          message: message
        });
      }
    } catch (error) {
      setSubmitMessage({
        type: 'error',
        message: 'Unexpected error occurred. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100 to-white py-12">
      {/* Font Awesome CDN */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInTop {
          from {
            opacity: 0;
            transform: translateY(-100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInBottom {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-left {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-right {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-top {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-bottom {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-fade-in {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-left.animate-visible {
          animation: slideInLeft 0.8s ease-out forwards;
        }
        
        .animate-right.animate-visible {
          animation: slideInRight 0.8s ease-out forwards;
        }
        
        .animate-top.animate-visible {
          animation: slideInTop 0.8s ease-out forwards;
        }
        
        .animate-bottom.animate-visible {
          animation: slideInBottom 0.8s ease-out forwards;
        }
        
        .animate-fade-in.animate-visible {
          animation: fadeIn 0.8s ease-out forwards;
        }
        
        /* Staggered delays for FAQ items */
        .faq-item:nth-child(1) { animation-delay: 0.1s; }
        .faq-item:nth-child(2) { animation-delay: 0.2s; }
        .faq-item:nth-child(3) { animation-delay: 0.3s; }
        .faq-item:nth-child(4) { animation-delay: 0.4s; }
        
        /* Staggered delays for form elements */
        .form-item:nth-child(1) { animation-delay: 0.1s; }
        .form-item:nth-child(2) { animation-delay: 0.2s; }
        .form-item:nth-child(3) { animation-delay: 0.3s; }
        .form-item:nth-child(4) { animation-delay: 0.4s; }
      `}</style>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Animate from top */}
        <div className="text-center mb-16 animate-top">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">
            Trade <span className="text-teal-600">Expert</span> Consultation
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get answers to your international trade questions and professional guidance for your business.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* FAQ Section - Animate from left */}
          <div className="bg-white rounded-2xl shadow-xl p-8 animate-left">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-question text-white text-lg"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`faq-item animate-fade-in border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <button
                    onClick={() => toggleQuestion(item.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    <span className="font-semibold text-gray-800 text-lg pr-4">
                      {item.question}
                    </span>
                    <i 
                      className={`fas fa-chevron-down text-teal-600 transition-transform duration-300 ${
                        activeQuestion === item.id ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  
                  <div 
                    className={`px-6 overflow-hidden transition-all duration-300 ${
                      activeQuestion === item.id ? 'max-h-96 pb-4' : 'max-h-0'
                    }`}
                  >
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Consultation Form - Animate from right */}
          <div className="bg-white rounded-2xl shadow-xl p-8 animate-right">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <i className="fas fa-headset text-white text-lg"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Free Consultation</h2>
            </div>

            {/* Submission Message */}
            {submitMessage && (
              <div className={`mb-6 p-4 rounded-lg border ${
                submitMessage.type === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              } animate-fade-in`}>
                <div className="flex items-center gap-2">
                  <i className={`fas ${
                    submitMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
                  }`}></i>
                  <span>{submitMessage.message}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Topic Selection - Animate fade in */}
              <div className="form-item animate-fade-in">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  I Would Like To Discuss
                </label>
                <select
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white"
                >
                  <option value="">Select an option</option>
                  {consultationTopics.map((topic) => (
                    <option key={topic} value={topic}>{topic}</option>
                  ))}
                </select>
              </div>

              {/* Name Field - Animate fade in */}
              <div className="form-item animate-fade-in">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Your Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Name"
                    required
                    className="w-full px-4 py-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 pl-12"
                  />
                  <i className="fas fa-user absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              {/* Email Field - Animate fade in */}
              <div className="form-item animate-fade-in">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Your Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    required
                    className="w-full px-4 py-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 pl-12"
                  />
                  <i className="fas fa-envelope absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              {/* Submit Button - Animate from bottom */}
              <div className="animate-bottom">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform shadow-lg flex items-center justify-center gap-2 ${
                    isSubmitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:from-teal-700 hover:to-teal-800 hover:scale-105 hover:shadow-xl'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      SENDING TO WHATSAPP...
                    </>
                  ) : (
                    <>
                      <i className="fab fa-whatsapp"></i>
                      SEND VIA WHATSAPP
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Additional Info */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <i className="fab fa-whatsapp text-green-500"></i>
                Your consultation request will be sent directly to our WhatsApp
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionAndFreeConsultation;